import { zoneOffsetAt } from '@/lib/zoneOffsetAt';

/**
 * Minutes added on each side of a day's bounds. A window used to fetch a day's rows can then only
 * over-fetch near a DST change, never drop a row; the caller keeps the rows whose `dayKeyInZone` /
 * `dayKeyFor` equals the day, so that filter stays the authority on membership.
 */
export const DAY_BOUNDS_MARGIN_MIN = 60;

const DAY_MS = 86_400_000;

const MARGIN_MS = DAY_BOUNDS_MARGIN_MIN * 60_000;

/**
 * True for a real calendar day written `YYYY-MM-DD` (2026-02-30 and 2026-13-01 are not). A `?day=`
 * value is user input: check it with this before computing a window from it.
 */
export function isDayKey(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const ms = Date.parse(`${value}T00:00:00Z`);
  return !Number.isNaN(ms) && new Date(ms).toISOString().slice(0, 10) === value;
}

/** UTC instant of local midnight starting `day` in `zone` (offset re-derived at the boundary for DST). */
function zoneMidnightMs(day: string, zone: string): number {
  const guess = Date.parse(`${day}T00:00:00Z`);
  const first = zoneOffsetAt(zone, new Date(guess)).offsetMin;
  const second = zoneOffsetAt(zone, new Date(guess - first * 60_000)).offsetMin;
  return guess - second * 60_000;
}

/**
 * The UTC window `[gte, lt)` that holds every instant whose day key is `day`, widened by
 * `DAY_BOUNDS_MARGIN_MIN` on each side. `zone` is an IANA zone (as `dayKeyInZone`) or a fixed offset
 * in minutes east of UTC (as `dayKeyFor`). A zone Intl rejects uses the UTC day, matching
 * `dayKeyInZone`'s fallback to the UTC date.
 */
export function dayBoundsUtc(day: string, zone: string | number): { gte: string; lt: string } {
  const dayStart = Date.parse(`${day}T00:00:00Z`);
  let start: number;
  let end: number;
  if (typeof zone === 'number') {
    start = dayStart - zone * 60_000;
    end = start + DAY_MS;
  } else {
    const next = new Date(dayStart + DAY_MS).toISOString().slice(0, 10);
    try {
      start = zoneMidnightMs(day, zone);
      end = zoneMidnightMs(next, zone);
    } catch {
      start = dayStart;
      end = dayStart + DAY_MS;
    }
  }
  return {
    gte: new Date(start - MARGIN_MS).toISOString(),
    lt: new Date(end + MARGIN_MS).toISOString(),
  };
}
